import React from 'react';
import { AIChatSession } from '@/services/api';
import { formatDate } from './types';
import LoadingStatus from '@/components/ui/LoadingStatus';

interface SavedSessionsListProps {
  savedSessions: AIChatSession[];
  loadingSessions: boolean;
  showSavedSessions: boolean;
  sessionId: string | null;
  deleteConfirm: string | null;
  onToggleShow: () => void;
  onLoadSession: (session: AIChatSession) => void;
  onDeleteSession: (id: string) => void;
  onSetDeleteConfirm: (id: string | null) => void;
}

const SavedSessionsList: React.FC<SavedSessionsListProps> = ({
  savedSessions,
  loadingSessions,
  showSavedSessions,
  sessionId,
  deleteConfirm,
  onToggleShow,
  onLoadSession,
  onDeleteSession,
  onSetDeleteConfirm,
}) => {
  return (
    <div className="mb-3">
      <button
        onClick={onToggleShow}
        className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
      >
        <svg
          className={`h-3.5 w-3.5 transform transition-transform ${showSavedSessions ? 'rotate-90' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        Saved Chats ({savedSessions.length})
      </button>

      {showSavedSessions && (
        <div className="mt-2 max-h-[200px] overflow-y-auto space-y-1.5">
          {loadingSessions && (
            <LoadingStatus label="Loading saved chats" className="py-1 text-xs" />
          )}
          {savedSessions.map(session => (
            <div
              key={session.id}
              role="button"
              tabIndex={0}
              className={`flex items-center justify-between p-2 rounded-md border text-sm cursor-pointer transition-colors ${
                sessionId === session.id
                  ? 'border-purple-400 dark:border-purple-600 bg-purple-50 dark:bg-purple-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
              onClick={() => onLoadSession(session)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onLoadSession(session); } }}
            >
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 dark:text-gray-100 truncate">{session.title}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {session.creatorName} &middot; {session.messageCount} msgs &middot; {formatDate(session.updatedAt)}
                </p>
              </div>
              <div className="flex-shrink-0 ml-2">
                {deleteConfirm === session.id ? (
                  <div className="flex items-center gap-1" onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()} role="group">
                    <button
                      onClick={() => onDeleteSession(session.id)}
                      className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 font-medium"
                    >
                      Delete
                    </button>
                    <button
                      onClick={() => onSetDeleteConfirm(null)}
                      className="text-xs text-gray-400 hover:text-gray-600"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={e => { e.stopPropagation(); onSetDeleteConfirm(session.id); }}
                    className="text-gray-300 dark:text-gray-600 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                    title="Delete session"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SavedSessionsList;
